import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import {
  ShieldCheck, Lock, CreditCard, Bell, Trash2,
  FileText, Scale, Mail, ArrowRight,
} from 'lucide-react';

/**
 * Trust Centre, public landing that collects the platform's safeguards and
 * legal documents in one place. Copy is deliberately conservative: we describe
 * what the product actually does and point to the ICO, without claiming
 * external certification or using marketing buzzwords like "bank-grade" or
 * "verified partner".
 */
export default function TrustCentre() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12" data-testid="trust-centre">
        {/* Hero */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 text-[#8a7218] rounded-full px-3 py-1 text-xs font-medium mb-4">
            <ShieldCheck className="h-3.5 w-3.5" />
            Trust Centre
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1A1A] mb-3" data-testid="trust-title">
            How we keep your account, data &amp; payments safe
          </h1>
          <p className="text-stone-500 leading-relaxed max-w-2xl">
            Zuriya Ltd takes the protection of your personal data and your payments seriously. This page explains
            the technical safeguards built into the platform and points you to every policy that governs how we
            handle your information.
          </p>
        </div>

        {/* Security */}
        <section className="mb-12" data-testid="trust-security">
          <h2 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-5 flex items-center gap-2">
            <Lock className="h-5 w-5 text-[#D4AF37]" />
            Account &amp; platform security
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <TrustCard
              icon={<ShieldCheck className="h-5 w-5 text-[#D4AF37]" />}
              title="Two-factor authentication"
              body="Every account can enable email-based 2FA with ten single-use backup codes from the Security tab. 2FA is mandatory for any account with administrative privileges."
            />
            <TrustCard
              icon={<Bell className="h-5 w-5 text-[#D4AF37]" />}
              title="Device login alerts"
              body="The first time your account is accessed from an unfamiliar device we email you with the device, location, and IP. You can revoke any recognised device in one tap, or sign out of every other session at once."
            />
            <TrustCard
              icon={<CreditCard className="h-5 w-5 text-[#D4AF37]" />}
              title="Secure payments via Stripe"
              body="All card payments, refunds, and provider payouts are processed by Stripe. Card numbers and security codes are entered directly into Stripe's hosted fields, they never reach our servers."
            />
            <TrustCard
              icon={<Trash2 className="h-5 w-5 text-[#D4AF37]" />}
              title="Account deletion &amp; 30-day retention"
              body="You can delete your account at any time from Security settings. Accounts are deactivated immediately and enter a 30-day grace window during which you can reactivate by contacting support. After that we permanently anonymise the record, keeping only the financial rows UK tax law requires."
            />
          </div>
          <p className="text-xs text-stone-500 mt-4">
            Additional controls include password hashing with bcrypt, httpOnly session cookies, sliding-window
            rate limits on every authentication endpoint, and Cloudflare edge protection.
          </p>
        </section>

        {/* Data protection */}
        <section className="mb-12" data-testid="trust-data-protection">
          <h2 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-5 flex items-center gap-2">
            <Scale className="h-5 w-5 text-[#D4AF37]" />
            Data protection
          </h2>
          <div className="card-surface p-6 space-y-3">
            <div className="mb-2 rounded-lg border border-[#D4AF37]/30 bg-[#FFFBEB] p-4 text-sm" data-testid="trust-ico-registration">
              <p className="font-medium text-[#1A1A1A] mb-1 flex items-center gap-2">
                <Scale className="h-4 w-4 text-[#D4AF37]" />
                ICO Data Protection Registration
              </p>
              <p className="text-stone-600">
                Zuriya Ltd is registered with the UK Information Commissioner&apos;s Office as a data controller.
              </p>
              <p className="text-stone-600 mt-1">
                Registration number: <strong className="font-mono">ZC178306</strong>
              </p>
              <p className="text-stone-500 text-xs mt-1">
                Renewed annually. Next renewal: 19 June 2027.
                {' '}
                <a
                  href="https://ico.org.uk/ESDWebPages/Entry/ZC178306"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#D4AF37] underline"
                >
                  Verify on ico.org.uk
                </a>
              </p>
            </div>
            <p className="text-sm text-stone-600 leading-relaxed">
              We aim to handle personal data in line with the UK General Data Protection Regulation (UK GDPR) and
              the Data Protection Act 2018. The
              <Link to="/privacy" className="text-[#D4AF37] underline mx-1">Privacy Policy</Link>
              sets out what we collect, the lawful bases we rely on, how long we keep it, and how you can
              exercise your rights, including subject access, rectification, erasure, restriction, objection,
              and data portability.
            </p>
            <p className="text-sm text-stone-600 leading-relaxed">
              If you have a concern that we have not resolved, you have the right to complain to the UK
              supervisory authority:
            </p>
            <div className="mt-2 rounded-lg border border-[#E6E4DD] bg-[#F9F6F0] p-4 text-sm">
              <p className="font-medium text-[#1A1A1A] mb-1">Information Commissioner&apos;s Office (ICO)</p>
              <p className="text-stone-600">Wycliffe House, Water Lane, Wilmslow, Cheshire SK9 5AF</p>
              <p className="text-stone-600">Helpline: 0303 123 1113</p>
              <p className="text-stone-600">
                Web: <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline">ico.org.uk/make-a-complaint</a>
              </p>
            </div>
            <p className="text-xs text-stone-500 leading-relaxed mt-3">
              For data queries that reach us first, write to
              <a href="mailto:privacy@zuriya.co.uk" className="text-[#D4AF37] underline mx-1">privacy@zuriya.co.uk</a>.
              We respond within one calendar month.
            </p>
          </div>
        </section>

        {/* Policies */}
        <section className="mb-12" data-testid="trust-hmrc">
          <h2 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-5 flex items-center gap-2">
            <Scale className="h-5 w-5 text-[#D4AF37]" />
            HMRC tax reporting
          </h2>
          <div className="card-surface p-6 space-y-3">
            <p className="text-sm text-stone-600 leading-relaxed">
              Zuriya is a Reporting Platform Operator under the UK&apos;s Reporting Rules for Digital Platforms
              (Schedule 23 Finance Act 2022, implementing the OECD Model Reporting Rules). This means we are
              legally required to collect tax-identifier information from every provider who earns through
              the platform and share it with HMRC once per year.
            </p>
            <p className="text-sm text-stone-600 leading-relaxed">
              For each reportable provider we collect a tax identifier (National Insurance number, Unique
              Taxpayer Reference, VAT number, or Companies House number, depending on entity type), legal name,
              registered address, and date of birth. We then submit an annual report to HMRC summarising each
              provider&apos;s aggregate platform earnings, refunds, and fees for the calendar year. The return is
              due 31 January each year for the prior year.
            </p>
            <p className="text-sm text-stone-600 leading-relaxed">
              Every January we email each reportable provider a copy of the figures we&apos;ve reported on their
              behalf so they can reconcile with their own Self Assessment. Providers retain full rights of
              correction under UK GDPR.
            </p>
            <p className="text-xs text-stone-500">
              Reference: <a href="https://www.gov.uk/guidance/reporting-rules-for-digital-platforms" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline">gov.uk, Reporting Rules for Digital Platforms</a>.
            </p>
          </div>
        </section>

        {/* Policies */}
        <section className="mb-12" data-testid="trust-policies">
          <h2 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-5 flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#D4AF37]" />
            Policies
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <PolicyLink
              to="/terms"
              title="Terms of Service"
              body="The agreement that governs your use of Zuriya, fees, bookings, cancellations, and provider obligations."
              testId="trust-link-terms"
            />
            <PolicyLink
              to="/privacy"
              title="Privacy Policy"
              body="What personal data we collect, why, how long we keep it, and your rights under UK GDPR."
              testId="trust-link-privacy"
            />
            <PolicyLink
              to="/refunds"
              title="Refund &amp; Cancellation Policy"
              body="The three cancellation tiers, card-on-file fee mechanics, refund timings, and the 48-hour dispute window."
              testId="trust-link-refunds"
            />
            <PolicyLink
              to="/cookies"
              title="Cookie Policy"
              body="Every cookie Zuriya sets, why it exists, and how long it lasts. No advertising or cross-site tracking cookies."
              testId="trust-link-cookies"
            />
          </div>
        </section>

        {/* Contact */}
        <section className="card-surface p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-[#D4AF37] mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-[#1A1A1A] mb-1">Still have questions?</p>
              <p className="text-sm text-stone-500">
                Service queries: <a href="mailto:support@zuriya.co.uk" className="text-[#D4AF37] underline">support@zuriya.co.uk</a>. Privacy &amp; data: <a href="mailto:privacy@zuriya.co.uk" className="text-[#D4AF37] underline">privacy@zuriya.co.uk</a>.
              </p>
            </div>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-1 bg-[#1A1A1A] text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-black transition-colors"
            data-testid="trust-contact-btn"
          >
            Contact us <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>
    </div>
  );
}

function TrustCard({ icon, title, body }) {
  return (
    <div className="card-surface p-5" data-testid="trust-card">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-[#F9F6F0] flex items-center justify-center shrink-0 border border-[#E6E4DD]">
          {icon}
        </div>
        <div>
          <h3 className="font-medium text-[#1A1A1A] mb-1">{title}</h3>
          <p className="text-sm text-stone-500 leading-relaxed">{body}</p>
        </div>
      </div>
    </div>
  );
}

function PolicyLink({ to, title, body, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="card-surface p-5 block hover:border-[#D4AF37]/40 hover:shadow-md transition-all group"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-medium text-[#1A1A1A] mb-1 group-hover:text-[#D4AF37] transition-colors">{title}</h3>
          <p className="text-sm text-stone-500 leading-relaxed">{body}</p>
        </div>
        <ArrowRight className="h-4 w-4 text-stone-400 group-hover:text-[#D4AF37] transition-colors shrink-0 mt-1" />
      </div>
    </Link>
  );
}
